import { useApolloClient } from '@apollo/client';
import { useEffect } from 'react';

import { graphql } from '__generated__';

import type { TargetUser } from 'common/accountActivationAndRecovery/types';
import type { AuthUser } from 'common/auth/types';
import { CIError } from 'common/error/error_util';
import { getCurrentLng, i18n, setLanguage } from 'common/i18n/i18n';
import { CILogger } from 'common/log/log.provider';
import { CIPageStatus } from 'common/viewcomp/general/ci_pagestatus';

export function useResumeActivation({
    activatedUser,
    targetUser,
    onFinish,
    onNeedsPassword,
}: {
    activatedUser: AuthUser | null;
    targetUser: TargetUser | null;
    onFinish: () => void;
    onNeedsPassword: () => void;
}): void {
    const client = useApolloClient();

    useEffect(() => {
        if (!activatedUser || targetUser?.type !== 'userPk') {
            return;
        }
        client
            .query({ query: GET_USER_SECURITY_QUESTION_1_ID, variables: { pk: targetUser.value } })
            .then((result): void => {
                if (result.data.getUser.securityQuestion1Id) {
                    onFinish();
                } else {
                    onNeedsPassword();
                }
            })
            .catch((err): void => {
                const error = CIError.fromGraphQLError(err);

                CILogger.getInstance().error('Failed to resolve which activation step to resume on', error);
                CIPageStatus.error(i18n.getFixedT(getCurrentLng(), 'global')('graphql.systemerror'));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}

export function useAdvanceAfterPasswordCreated({
    isOnPasswordStep,
    activatedUser,
    isPasswordReset,
    onFinish,
    onAdvanceToSecurity,
}: {
    isOnPasswordStep: boolean;
    activatedUser: AuthUser | null;
    isPasswordReset: boolean;
    onFinish: () => void;
    onAdvanceToSecurity: () => void;
}): void {
    useEffect(() => {
        if (!isOnPasswordStep || !activatedUser) {
            return;
        }
        setLanguage(activatedUser.preferredLanguage || 'en-US', () => {
            if (isPasswordReset) {
                onFinish();
            } else {
                onAdvanceToSecurity();
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOnPasswordStep, activatedUser, isPasswordReset]);
}

const GET_USER_SECURITY_QUESTION_1_ID = graphql(`
    query GetUserSecurityQuestion1Id($pk: String!) {
        getUser(pk: $pk) {
            securityQuestion1Id
        }
    }
`);
